import type { FilePondFile } from 'filepond';
import type { FilePond } from 'react-filepond';
import React, { type SetStateAction } from 'react';
import { UPLOAD_EVIDENCE } from 'src/api';
import {
  HelpArticleLink,
  InfoFooter,
  InfoIconWrapper,
  InfoText,
} from 'src/components/molecules/FileUploadDropZone/FileUploadDropZone.styles';
import {
  FileUpload,
  type File as EvidenceUploadFile,
} from 'src/components/moleculesLegacy/FileUpload';
import {
  DynamicQuestionDescription,
  DynamicQuestionFileUploadShell,
} from './DynamicQuestion.styles';
import { DYNAMIC_QUESTION_FILE_UPLOAD_LABEL_IDLE } from './DynamicQuestionProps';

export type DynamicQuestionFileUploadFieldProps = {
  baseTestId: string;
  renderLabel: (labelHtmlFor?: string) => React.ReactNode;
  description?: string;
  reportIdForEvidenceUpload: string | null | undefined;
  fileUploadHelpArticleUrl?: string;
  evidenceFiles: EvidenceUploadFile[];
  handleSetPond: (instance: SetStateAction<FilePond | null>) => void;
  syncEvidenceFilesFromPond: () => void;
  handleRemoveFileFromPond: (file: FilePondFile) => void;
  /** When true, do not re-sync answers from FilePond after removing a file that has `qaAttachmentId` metadata (QA delete uses optimistic cache/state updates). */
  skipEvidenceSyncAfterQaAttachmentRemove?: boolean;
};

export const DynamicQuestionFileUploadField: React.FC<
  DynamicQuestionFileUploadFieldProps
> = ({
  baseTestId,
  renderLabel,
  description,
  reportIdForEvidenceUpload,
  fileUploadHelpArticleUrl,
  evidenceFiles,
  handleSetPond,
  syncEvidenceFilesFromPond,
  handleRemoveFileFromPond,
  skipEvidenceSyncAfterQaAttachmentRemove = false,
}) => {
  const uploadServerUrl = reportIdForEvidenceUpload
    ? `${UPLOAD_EVIDENCE(reportIdForEvidenceUpload)}`
    : null;

  return (
    <DynamicQuestionFileUploadShell data-testid={baseTestId}>
      {renderLabel()}
      {description && (
        <DynamicQuestionDescription>{description}</DynamicQuestionDescription>
      )}
      {uploadServerUrl == null ? (
        <DynamicQuestionDescription data-testid={`${baseTestId}-file-upload`}>
          Save your draft to upload files. Files are stored with this report as
          supporting evidence.
          {fileUploadHelpArticleUrl != null && (
            <>
              {' '}
              For supported file types, see this{' '}
              <a
                href={fileUploadHelpArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${baseTestId}-file-upload-help-article-link`}
              >
                help article
              </a>
              .
            </>
          )}
        </DynamicQuestionDescription>
      ) : (
        <>
          <DynamicQuestionFileUploadShell
            data-testid={`${baseTestId}-file-upload`}
          >
            <FileUpload
              files={evidenceFiles}
              server={uploadServerUrl}
              setPond={handleSetPond}
              onprocessfiles={syncEvidenceFilesFromPond}
              onupdatefiles={syncEvidenceFilesFromPond}
              onremovefile={(error, file) => {
                if (error) {
                  syncEvidenceFilesFromPond();
                  return;
                }
                const qaId = file?.getMetadata?.('qaAttachmentId') as
                  | string
                  | undefined;
                if (
                  skipEvidenceSyncAfterQaAttachmentRemove &&
                  qaId != null &&
                  qaId.trim() !== ''
                ) {
                  return;
                }
                syncEvidenceFilesFromPond();
              }}
              onRemoveFileFromPond={handleRemoveFileFromPond}
              labelIdle={DYNAMIC_QUESTION_FILE_UPLOAD_LABEL_IDLE}
              stylePanelLayout="compact"
            />
          </DynamicQuestionFileUploadShell>
          {fileUploadHelpArticleUrl != null && (
            <InfoFooter>
              <InfoIconWrapper aria-hidden>i</InfoIconWrapper>
              <InfoText>
                For more information on supported file types, please read this{' '}
                <HelpArticleLink
                  href={fileUploadHelpArticleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${baseTestId}-file-upload-help-article-link`}
                >
                  Help article
                </HelpArticleLink>
              </InfoText>
            </InfoFooter>
          )}
        </>
      )}
    </DynamicQuestionFileUploadShell>
  );
};
